import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import { licensePlateNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { useScrollToTopOnLoad } from "@/utils/hooksUtils";
import { isDeepEqual, removeStringWhiteSpaces } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { FieldData } from "@rc-component/form/es/interface";
import { App as AntApp, Card, Col, Form, Radio, Row } from "antd";
import { RadioChangeEvent } from "antd/lib/radio/interface";
import { t } from "i18next";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useValidateClient } from "../../../../../../common/api/queries";
import { rowGutter } from "../../../../../../common/constants";
import { Permission } from "../../../../../../common/security/enums";
import type { FieldConstraintViolation } from "../../../../../../common/types";
import { useHasPermissions } from "../../../../../auth/hooks";
import { CalcValidationGroup, ClientFormType, ClientType } from "../../../../../client/enums";
import { Client, CreateUpdateContractClient } from "../../../../../client/types";
import {
  clientToCreateUpdateContractClient,
  createUpdateContractClientToClient,
  processClientsDataViolations,
  resolveClientIdentifier,
} from "../../../../../client/utils";
import { DashboardNoticePosition } from "../../../../../dashboard/enums";
import { CalcType } from "../../../../enums";
import { CALC_ROUTE_PATHS } from "../../../../paths";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { CalcNoticesIconView } from "../../../components/CalcNoticesIconView";
import type {
  VehicleCalc,
  VehicleCalcForm,
  VehicleCalcType,
  VehicleCalcVehicleData,
  VehicleFormClients,
} from "../../types";
import { VEHICLE_CATEGORY_M1_N1 } from "../../utils";
import { VehicleCalcClientsDataSection } from "./sections/VehicleCalcClientsDataSection";
import { VehicleCalcGeneralAndReinsurancesDataSection } from "./sections/VehicleCalcGeneralAndReinsurancesDataSection";
import { VehicleCalcVehicleDataSection } from "./sections/VehicleCalcVehicleDataSection";

const resolvePageTitle = (type?: CalcType) => {
  return type ? t("calc.vehicle.titles.calculation." + type) : t("calc.vehicle.titles.noSelectedCalcType");
};

const resolveCalcTypeIcon = (type: CalcType) => {
  switch (type) {
    case CalcType.MTPL:
      return "dashboard";
    case CalcType.CRASH:
      return "car";
    case CalcType.MTPL_CRASH:
      return "safety";
    default:
      return "dashboard";
  }
};

interface Props {
  calcData?: VehicleCalc;
  formErrors: FieldConstraintViolation[];
  clientsViolationErrors: Map<ClientFormType, FieldConstraintViolation[]>;
  setIsFormChanged: () => void;
  holder?: Partial<Client>;
  onCalculationFormSubmit: (calcData: VehicleCalc) => void;
  onClientChange: (type: ClientFormType, client?: Partial<Client>) => VehicleFormClients;
  onClientsViolationErrorsChange: (errors: Map<ClientFormType, FieldConstraintViolation[]>) => void;
  onResetCalculatorClick: (onSuccess: () => void) => void;
  goToCalcResults?: () => void;
}

export const VEHICLE_CALC_HOLDER_INDEX_MAP = new Map<ClientFormType, number>([[ClientFormType.HOLDER, 0]]);

export const VehicleCalcWrapper = ({
  calcData,
  formErrors,
  clientsViolationErrors,
  setIsFormChanged,
  holder,
  onCalculationFormSubmit,
  onClientChange,
  onClientsViolationErrorsChange,
  onResetCalculatorClick,
  goToCalcResults,
}: Props) => {
  useScrollToTopOnLoad();
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<VehicleCalcForm>();
  const calcType = Form.useWatch("type", form);

  const clientValidation = useValidateClient();

  const canCalculateMtpl = useHasPermissions(Permission.MTPL_CALCULATE);
  const canCalculateCrash = useHasPermissions(Permission.CRASH_CALCULATE);

  useEffect(() => {
    if (calcData) {
      const licensePlate = calcData.vehicleData.licensePlate;

      const holder = createUpdateContractClientToClient(
        calcData.clientsData.clients[calcData.clientsData.holderIndex] as CreateUpdateContractClient,
      );

      const { clients, holderIndex, ...clientsData } = calcData.clientsData;

      form.setFieldsValue({
        ...calcData,
        vehicleData: {
          ...calcData.vehicleData,
          licensePlate: licensePlate ? licensePlateNormalizeFunction(licensePlate) : undefined,
        },
        clientsData: {
          ...clientsData,
          holderIdentifier: holder ? resolveClientIdentifier(holder) : undefined,
        },
      });
    } else {
      form.resetFields();
    }
  }, [calcData]);

  useEffect(() => {
    if (formErrors.length) {
      setErrorsToForm(form, "calc.vehicle.attrs", formErrors);
    }
  }, [formErrors]);

  const handleInsuranceTypeChange = (e: RadioChangeEvent) => {
    const calcType = e.target.value as VehicleCalcType;
    const vehicleData = form.getFieldValue(["vehicleData"]) as VehicleCalcVehicleData;
    switch (calcType) {
      case CalcType.MTPL:
        form.setFieldsValue({
          vehicleData: {
            odometer: undefined,
            serviceType: undefined,
            garaged: undefined,
            price:
              vehicleData.newVehicle && VEHICLE_CATEGORY_M1_N1.includes(vehicleData.category)
                ? vehicleData.price
                : undefined,
            generalPrice: undefined,
            buyingPrice: undefined,
          },
          clientsData: {
            holderHasNeverBeenCrashInsured: false,
          },
          generalData: {
            crossSelling: {
              kooperativaContract: false,
            },
            gapDuration: undefined,
            gapCoverageLimit: undefined,
          },
          reinsurancesData: {
            gap: false,
            replacementVehicle: false,
            generaliAbroadVehicleRepair: false,
            generaliElectroPackage: false,
            koopExtendedWarranty: false,
          },
        });
        break;
      case CalcType.CRASH:
        form.setFieldsValue({
          vehicleData: {
            steeringWheelOnTheRight: false,
            emergencyBrakingSystem: false,
            parkingAssistant: false,
          },
          reinsurancesData: {
            animal: false,
            element: false,
            elementAmount: undefined,
            theft: false,
            vandalism: false,
            injury: false,
            injuryAmount: undefined,
            koopExtendedWarranty: false,
            uniqaSmallDamage: false,
            uniqaTotalDamage: false,
            uniqaLawyerAssistance: false,
            uniqaSidecarMtpl: false,
            uniqaChildrenInjury: false,
          },
        });
        break;
      case CalcType.MTPL_CRASH:
        form.setFieldsValue({
          vehicleData: {
            steeringWheelOnTheRight: false,
            emergencyBrakingSystem: false,
            parkingAssistant: false,
          },
          generalData: {
            crossSelling: {
              kooperativaContract: false,
              unionHealthContract: false,
            },
          },
          reinsurancesData: {
            animal: false,
            element: false,
            elementAmount: undefined,
            theft: false,
            vandalism: false,
            injuryAmount: undefined,
            generaliAbroadVehicleRepair: false,
            generaliElectroPackage: false,
            uniqaSmallDamage: false,
            uniqaTotalDamage: false,
          },
        });
        break;
    }
  };

  const handleFormFieldsChange = (changedFields?: FieldData[]) => {
    if (changedFields?.length) {
      setIsFormChanged();
    }
  };

  const handleCalculationFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!holder || clientsViolationErrors.has(ClientFormType.HOLDER)) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else {
          onCalculationFormSubmit(processAndGetCalcFormData(values));
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const handleHolderChange = (client?: Partial<Client>) => {
    if (!isDeepEqual(holder, client)) {
      setIsFormChanged();
    }

    onClientChange(ClientFormType.HOLDER, client);

    if (client) {
      clientValidation.mutate(
        {
          prefix: `clientsData.clients[0]`,
          client: clientToCreateUpdateContractClient(client),
          validationGroup: CalcValidationGroup.CALCULATE,
        },
        {
          onError: (error) => {
            if (error.violations) {
              onClientsViolationErrorsChange(
                processClientsDataViolations(VEHICLE_CALC_HOLDER_INDEX_MAP, "clientsData.clients", error.violations),
              );
            }
          },
        },
      );

      if (client.type !== ClientType.NATURAL) {
        form.setFieldsValue({
          clientsData: {
            holderIsDisabledPerson: false,
            disabledPersonIdCardNumber: undefined,
          },
          generalData: {
            crossSelling: {
              unionHealthContract: false,
            },
          },
        });
      }
    } else {
      form.setFieldsValue({
        generalData: {
          crossSelling: {
            unionHealthContract: false,
          },
        },
      });
    }
  };

  const handleHolderViolationErrorsDelete = () => {
    onClientsViolationErrorsChange(new Map<ClientFormType, FieldConstraintViolation[]>());
  };

  const processAndGetCalcFormData = (calcData: VehicleCalcForm): VehicleCalc => {
    const { holderIdentifier, ...clientsData } = calcData.clientsData;

    const calcClientsData = {
      ...clientsData,
      holderIndex: 0,
      clients: holder ? [clientToCreateUpdateContractClient(holder)] : [],
    };

    return {
      ...calcData,
      vehicleData: {
        ...calcData.vehicleData,
        licensePlate: removeStringWhiteSpaces(calcData.vehicleData.licensePlate),
      },
      clientsData: calcClientsData,
      reinsurancesData: {
        ...calcData.reinsurancesData,
        injuryAmount: calcData.type === CalcType.MTPL ? calcData.reinsurancesData.injuryAmount : undefined,
      },
    };
  };

  const allowedCalcTypes =
    canCalculateMtpl && canCalculateCrash
      ? [CalcType.MTPL, CalcType.CRASH, CalcType.MTPL_CRASH]
      : [canCalculateMtpl ? CalcType.MTPL : CalcType.CRASH];

  return (
    <>
      <Form form={form} layout="vertical" name="vehicleCalcForm" onFieldsChange={handleFormFieldsChange}>
        <Card
          title={<h2>{resolvePageTitle(calcType)}</h2>}
          className="card-box card-box--inner-extra"
          extra={
            <>
              <CalcNoticesIconView position={DashboardNoticePosition.VEHICLE_CALC} />
              <Link to={CALC_ROUTE_PATHS.vehicleDraft.to}>
                <SecondaryButton icon={<AntIcon type="form" />}>{t("calc.actions.showDrafts")}</SecondaryButton>
              </Link>
            </>
          }
        >
          <Row gutter={rowGutter} justify="center">
            <Col span={12} className="center-align">
              <Form.Item name="type" rules={[validations.notNull]} initialValue={allowedCalcTypes[0]}>
                <Radio.Group
                  block
                  optionType="button"
                  size="large"
                  buttonStyle="solid"
                  onChange={handleInsuranceTypeChange}
                >
                  {allowedCalcTypes.map((type) => (
                    <Radio.Button
                      value={type}
                      key={type}
                      style={{ height: 60, display: "flex", justifyContent: "center" }}
                    >
                      <AntIcon size="huge" type={resolveCalcTypeIcon(type)} />
                      <div style={{ fontSize: 16, lineHeight: "8px" }}>{t("calc.enums.calcType." + type)}</div>
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <VehicleCalcVehicleDataSection />

        <VehicleCalcClientsDataSection
          holder={holder}
          clientsViolationErrors={clientsViolationErrors}
          onHolderChange={handleHolderChange}
          onHolderViolationErrorsDelete={handleHolderViolationErrorsDelete}
        />

        <VehicleCalcGeneralAndReinsurancesDataSection holder={holder} />
      </Form>

      <CalcNavigation
        onCalculate={handleCalculationFormSubmit}
        onResetCalculatorClick={() => onResetCalculatorClick(() => form.resetFields())}
        goToCalcResults={goToCalcResults}
      />
    </>
  );
};
